/**
 * FundingManagementFields — Phase 4 (Funding Method Routing)
 * Explicit self / plan / agency selection on a Service Agreement. Blocking of NOT_SET applies while the
 * backend flag funding_routing_gate_enabled is ON (registry default ON; admin can turn it off).
 */
import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { isFundingRoutingOn } from '@/lib/fundingRoutingFlag';

export const FUNDING_TYPES = [
  { value: 'NOT_SET', label: 'Not yet set' },
  { value: 'SELF_MANAGED', label: 'Self-managed — invoice participant / nominee' },
  { value: 'PLAN_MANAGED', label: 'Plan-managed — invoice Plan Manager' },
  { value: 'AGENCY_MANAGED', label: 'Agency-managed (NDIA) — claim via NDIS portal' },
];

const FUNDING_COLORS = {
  NOT_SET: 'bg-slate-100 text-slate-600 border-slate-300',
  NOT_SET_BLOCKING: 'bg-red-100 text-red-700 border-red-300',
  SELF_MANAGED: 'bg-sky-100 text-sky-700 border-sky-300',
  PLAN_MANAGED: 'bg-indigo-100 text-indigo-700 border-indigo-300',
  AGENCY_MANAGED: 'bg-emerald-100 text-emerald-700 border-emerald-300',
};

export const EMPTY_FUNDING = {
  type: 'NOT_SET',
  plan_manager: { name: '', organisation: '', email: '', phone: '', address: '', abn: '' },
  bill_to: { name: '', organisation: '', email: '', phone: '', address: '', relationship: '' },
};

export function fundingFromAgreement(a) {
  const fm = a?.funding_management || {};
  return {
    type: fm.type || 'NOT_SET',
    plan_manager: { ...EMPTY_FUNDING.plan_manager, ...(fm.plan_manager || {}) },
    bill_to: { ...EMPTY_FUNDING.bill_to, ...(fm.bill_to || {}) },
  };
}

export function FundingBadge({ type }) {
  const t = type || 'NOT_SET';
  const colorKey = t === 'NOT_SET' && isFundingRoutingOn() ? 'NOT_SET_BLOCKING' : t;
  return (
    <Badge variant="outline" className={`${FUNDING_COLORS[colorKey] || ''} text-xs`} data-testid={`agreement-funding-${t}`}>
      {t.replace('_', ' ')}
    </Badge>
  );
}

function ContactFields({ value, onChange, prefix, showAbn, showRelationship }) {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  return (
    <div className="grid grid-cols-2 gap-2">
      <div><Label className="text-xs">Name</Label><Input value={value.name || ''} onChange={set('name')} data-testid={`${prefix}-name`} /></div>
      <div><Label className="text-xs">Organisation</Label><Input value={value.organisation || ''} onChange={set('organisation')} data-testid={`${prefix}-organisation`} /></div>
      <div><Label className="text-xs">Email</Label><Input type="email" value={value.email || ''} onChange={set('email')} data-testid={`${prefix}-email`} /></div>
      <div><Label className="text-xs">Phone</Label><Input value={value.phone || ''} onChange={set('phone')} data-testid={`${prefix}-phone`} /></div>
      <div className="col-span-2"><Label className="text-xs">Address</Label><Input value={value.address || ''} onChange={set('address')} data-testid={`${prefix}-address`} /></div>
      {showAbn && <div><Label className="text-xs">ABN</Label><Input value={value.abn || ''} onChange={set('abn')} data-testid={`${prefix}-abn`} /></div>}
      {showRelationship && <div><Label className="text-xs">Relationship</Label><Input placeholder="Nominee / Guardian" value={value.relationship || ''} onChange={set('relationship')} data-testid={`${prefix}-relationship`} /></div>}
    </div>
  );
}

export function FundingManagementFields({ value, onChange, testIdPrefix = 'agreement-funding' }) {
  const routingOn = isFundingRoutingOn();
  return (
    <div className="space-y-2 rounded-md border p-3">
      <Label>Funding management{routingOn ? ' *' : ' (optional)'}</Label>
      <Select value={value.type} onValueChange={(v) => onChange({ ...value, type: v })}>
        <SelectTrigger data-testid={`${testIdPrefix}-type`}><SelectValue /></SelectTrigger>
        <SelectContent>
          {FUNDING_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
        </SelectContent>
      </Select>
      {value.type === 'NOT_SET' && (routingOn ? (
        <p className="text-xs text-red-600" data-testid={`${testIdPrefix}-not-set-warning`}>
          Invoices and NDIA claims are blocked (FUNDING_METHOD_NOT_SET) until one option is chosen.
        </p>
      ) : (
        <p className="text-xs text-muted-foreground" data-testid={`${testIdPrefix}-not-set-info`}>
          Informational for now — invoicing and claiming are not affected. Recording it ahead of time prepares the agreement for funding-method routing.
        </p>
      ))}
      {value.type === 'PLAN_MANAGED' && (
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">Plan Manager (name or organisation, plus email or phone, required)</p>
          <ContactFields value={value.plan_manager} onChange={(v) => onChange({ ...value, plan_manager: v })} prefix={`${testIdPrefix}-pm`} showAbn />
        </div>
      )}
      {value.type === 'SELF_MANAGED' && (
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">Bill-to nominee (optional — leave blank to invoice the participant using their profile contact details)</p>
          <ContactFields value={value.bill_to} onChange={(v) => onChange({ ...value, bill_to: v })} prefix={`${testIdPrefix}-nominee`} showRelationship />
        </div>
      )}
    </div>
  );
}

export function fundingPayload(value) {
  const clean = (o) => Object.fromEntries(Object.entries(o || {}).filter(([, v]) => v !== '' && v != null));
  return {
    type: value.type,
    plan_manager: value.type === 'PLAN_MANAGED' ? clean(value.plan_manager) : null,
    bill_to: value.type === 'SELF_MANAGED' && Object.keys(clean(value.bill_to)).length ? clean(value.bill_to) : null,
  };
}
